import { type AppConfig, type UpdateAppSettings, appConfigSchema } from '@xphoto/shared';
import { apiFetch } from '../lib/http';

/**
 * Client typé de la CONFIGURATION GLOBALE (Incrément B — coupe-circuit IA). Chaque
 * réponse est RE-PARSÉE par son schéma partagé `@xphoto/shared`. Session same-origin.
 */

/**
 * L'appel a-t-il échoué sur un CONFLIT DE VERSION (audit A6) — les réglages ont été
 * modifiés entre la lecture et l'enregistrement ? L'UI doit alors proposer de RECHARGER,
 * pas afficher une panne.
 */
export function isStaleUpdate(err: unknown): boolean {
  return err instanceof AppConfigApiError && err.status === 409 && err.code === 'STALE_UPDATE';
}

/** Erreur d'appel API : statut HTTP + code normalisé. */
export class AppConfigApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'AppConfigApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON : code générique.
  }
  return 'UNKNOWN';
}

/** `GET /api/app-config` — état global lu par la SPA (tout rôle authentifié). */
export async function fetchAppConfig(signal?: AbortSignal): Promise<AppConfig> {
  const res = await apiFetch('/api/app-config', {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  if (!res.ok) throw new AppConfigApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}

/** `PATCH /api/app-settings` — bascule le coupe-circuit IA (superadmin). */
export async function updateAppSettings(aiEnabled: boolean): Promise<AppConfig> {
  const payload: UpdateAppSettings = { aiEnabled };
  const res = await apiFetch('/api/app-settings', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new AppConfigApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}

/**
 * `PATCH /api/app-settings` — renomme le site (superadmin, LOT 3).
 *
 * `expectedUpdatedAt` (VERROUILLAGE OPTIMISTE, audit A6) : l'`updatedAt` lu par ce
 * formulaire. Fourni, le serveur REFUSE l'écriture en 409 `STALE_UPDATE` si un autre
 * éditeur a modifié les réglages entre-temps, au lieu de l'écraser en silence.
 */
export async function updateAppName(
  appName: string,
  expectedUpdatedAt?: string,
): Promise<AppConfig> {
  const payload: UpdateAppSettings = {
    appName,
    ...(expectedUpdatedAt !== undefined ? { expectedUpdatedAt } : {}),
  };
  const res = await apiFetch('/api/app-settings', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new AppConfigApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}

/**
 * `PATCH /api/app-settings` — configure la passerelle d'authentification (superadmin,
 * ADR-0008). `null` = DÉSACTIVER (le champ vidé). L'URL est validée côté serveur
 * (http/https) → 400 `VALIDATION` si elle est inexploitable.
 */
export async function updateAccessGatewayUrl(
  url: string | null,
  expectedUpdatedAt?: string,
): Promise<AppConfig> {
  const payload: UpdateAppSettings = {
    accessGatewayUrl: url,
    ...(expectedUpdatedAt !== undefined ? { expectedUpdatedAt } : {}),
  };
  const res = await apiFetch('/api/app-settings', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new AppConfigApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}

/**
 * `PATCH /api/app-settings` — règle l'agrandissement (%) des petites photos dans la
 * visionneuse (superadmin). L'entier est borné côté serveur (`SMALL_IMAGE_UPSCALE_*`)
 * → 400 `VALIDATION` hors bornes.
 */
export async function updateSmallImageUpscalePercent(
  percent: number,
  expectedUpdatedAt?: string,
): Promise<AppConfig> {
  const payload: UpdateAppSettings = {
    smallImageUpscalePercent: percent,
    ...(expectedUpdatedAt !== undefined ? { expectedUpdatedAt } : {}),
  };
  const res = await apiFetch('/api/app-settings', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new AppConfigApiError(res.status, await errorCodeOf(res));
  return appConfigSchema.parse(await res.json());
}
